import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  BasicDialogService,
  DialogPresets,
} from '@principle-theorem/ng-shared';
import {
  Invoice,
  MohClaimFundingRequest,
  Patient,
} from '@principle-theorem/principle-core';
import {
  FundingRequestStatus,
  IAppointment,
  IMohClaimFundingRequest,
  IMohPreApprovalFundingRequest,
  IPatient,
  IPractice,
  MohClaimType,
  MohGenerateFormRequest,
  MohGenerateFormRequestType,
  PatientRelationshipType,
  TransactionStatus,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  Firestore,
  WithRef,
} from '@principle-theorem/shared';
import { combineLatest, Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import {
  IMohSendClaimDialogData,
  IMohSendClaimForm,
  SdsClaimForm,
} from '../components/moh/moh-send-claim-dialog/moh-send-claim-dialog.component';
import { MohSignFormDialogComponent } from '../components/moh/moh-sign-form-dialog/moh-sign-form-dialog.component';
import { MohClaimApprovalService } from './moh-claim-approval.service';
import { MohClaimPdfDataBuilder } from './pdf-data/moh-claim-pdf-data-builder';

@Injectable()
export class MohClaimsService {
  private _dialog = inject(MatDialog);
  private _basicDialog = inject(BasicDialogService);
  private _snackBar = inject(MatSnackBar);
  private _approvalService = inject(MohClaimApprovalService);

  getMohClaims$(
    practice$: Observable<WithRef<IPractice>>,
    patient$: Observable<WithRef<IPatient> | undefined>
  ): Observable<WithRef<IMohClaimFundingRequest>[]> {
    return combineLatest([practice$, patient$]).pipe(
      switchMap(([practice, patient]) =>
        patient
          ? Patient.withPatientRelationships$(
              patient,
              [PatientRelationshipType.DuplicatePatient],
              (relatedPatient) =>
                MohClaimFundingRequest.byPatient$(practice, relatedPatient)
            )
          : MohClaimFundingRequest.all$(practice)
      )
    );
  }

  async markAsApproved(
    claimRequest: WithRef<IMohClaimFundingRequest>
  ): Promise<void> {
    if (!claimRequest.data.transactionRef || !claimRequest.data.invoiceRef) {
      return;
    }

    const transaction = await Firestore.getDoc(
      claimRequest.data.transactionRef
    );
    const invoice = await Firestore.getDoc(claimRequest.data.invoiceRef);
    const claim = Invoice.findHealthcareClaimForTransaction(
      invoice,
      transaction
    );
    if (!claim) {
      return;
    }

    const approved = await this._approvalService.processApproval({
      invoice,
      transaction,
      claimRequest,
      claim,
    });
    if (!approved) {
      return;
    }
    this._snackBar.open('Claim marked as Approved');
  }

  async markAsDeclined(claim: WithRef<IMohClaimFundingRequest>): Promise<void> {
    await Firestore.patchDoc(claim.ref, {
      status: FundingRequestStatus.Declined,
    });
    if (claim.data.transactionRef) {
      await Firestore.patchDoc(claim.data.transactionRef, {
        status: TransactionStatus.Failed,
      });
    }
    this._snackBar.open('Claim marked as Declined');
  }

  async markAsCancelled(
    claim: WithRef<IMohClaimFundingRequest>
  ): Promise<void> {
    if (claim.status === FundingRequestStatus.Submitted) {
      const confirmed = await this._basicDialog.confirm({
        title: 'Cancel Submitted Claim Request',
        prompt:
          'This claim request has been submitted to MOH. Are you sure you want to cancel?',
        submitColor: 'warn',
        submitLabel: 'Cancel',
      });
      if (!confirmed) {
        return;
      }
    }
    await Firestore.patchDoc(claim.ref, {
      status: FundingRequestStatus.Cancelled,
    });
    if (claim.data.transactionRef) {
      await Firestore.patchDoc(claim.data.transactionRef, {
        status: TransactionStatus.Failed,
      });
    }
    this._snackBar.open('Claim marked as Cancelled');
  }

  async signAndSubmitClaimRequest(
    claim: WithRef<IMohClaimFundingRequest>,
    approvalRequestOverride?: WithRef<IMohPreApprovalFundingRequest>
  ): Promise<DocumentReference<IMohClaimFundingRequest> | undefined> {
    const submittedRef = await this._dialog
      .open<
        MohSignFormDialogComponent,
        WithRef<IMohClaimFundingRequest>,
        DocumentReference<IMohClaimFundingRequest>
      >(
        MohSignFormDialogComponent,
        DialogPresets.almostFullscreen({ data: claim })
      )
      .afterClosed()
      .toPromise();
    if (!submittedRef) {
      return;
    }

    await this._updateApprovalRequest(claim, approvalRequestOverride);
    return submittedRef;
  }

  async buildClaimFormRequest(
    data: IMohSendClaimDialogData,
    claimFormData: IMohSendClaimForm,
    sdsForm: SdsClaimForm,
    patient: WithRef<IPatient>
  ): Promise<MohGenerateFormRequest | undefined> {
    const appointment = await Invoice.getAssociatedAppointment({
      ref: data.invoice.ref,
    });

    switch (claimFormData.claimType) {
      case MohClaimType.OHSA:
      case MohClaimType.HighCaries:
        return this._buildOhsaAndHighCariesRequest(
          data,
          claimFormData,
          patient,
          appointment
        );
      case MohClaimType.SDS:
        return this._buildSdsRequest(
          data,
          sdsForm,
          claimFormData,
          patient,
          appointment
        );
      default:
        return;
    }
  }

  private _buildOhsaAndHighCariesRequest(
    dialogData: IMohSendClaimDialogData,
    claimFormData: IMohSendClaimForm,
    patient: WithRef<IPatient>,
    appointment?: WithRef<IAppointment>
  ): MohGenerateFormRequest | undefined {
    const data = MohClaimPdfDataBuilder.getOhsaClaimData(
      dialogData,
      claimFormData,
      patient,
      appointment
    );
    if (!data) {
      return;
    }

    return {
      type: MohGenerateFormRequestType.OhsaClaim,
      requestRef: dialogData.claimRequestRef,
      data,
    };
  }

  private _buildSdsRequest(
    dialogData: IMohSendClaimDialogData,
    sdsForm: SdsClaimForm,
    claimFormData: IMohSendClaimForm,
    patient: WithRef<IPatient>,
    appointment?: WithRef<IAppointment>
  ): MohGenerateFormRequest | undefined {
    const data = MohClaimPdfDataBuilder.getSdsClaimData(
      dialogData,
      patient,
      sdsForm,
      claimFormData,
      appointment
    );
    if (!data) {
      return;
    }

    return {
      type: MohGenerateFormRequestType.SdsClaim,
      requestRef: dialogData.claimRequestRef,
      data,
    } as MohGenerateFormRequest;
  }

  private async _updateApprovalRequest(
    claim: WithRef<IMohClaimFundingRequest>,
    approvalRequestOverride?: WithRef<IMohPreApprovalFundingRequest>
  ): Promise<void> {
    const approvalRequest =
      approvalRequestOverride ??
      (claim.data.approvalRequestRef
        ? await Firestore.getDoc(claim.data.approvalRequestRef)
        : undefined);
    if (!approvalRequest) {
      return;
    }
    await Firestore.patchDoc(approvalRequest.ref, {
      status: FundingRequestStatus.Redeemed,
      data: {
        ...approvalRequest.data,
        mohClaimRef: claim.ref,
      },
    });
  }
}
